import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { RiAddLine, RiRefreshLine } from '@remixicon/react';

import { Badge } from '@/design-system/badge';
import { Button } from '@/design-system/button';
import { Card } from '@/design-system/card';
import {
  EmptyState,
  EmptyStateActions,
  EmptyStateDescription,
  EmptyStateTitle,
} from '@/design-system/empty-state';
import { FormModal } from '@/design-system/form-modal';
import { SearchField } from '@/design-system/search-field';
import { SearchSelect } from '@/design-system/search-select';
import { Switch } from '@/design-system/switch';
import { TableData, type TableDataColumn } from '@/design-system/table-data';
import { toast } from '@/design-system/toaster';
import { useUrlBool, useUrlParam, useUrlSearch } from '@/hooks/urlState';
import { useProvidersDropdown } from '@/pages/shipments/components/useProvidersDropdown';
import { formatRupiah } from '@/pages/ujp/format';
import { sumChargedLegs } from '@/services/api/routeStopKind';
import { ujpAPI } from '@/services/api/ujp';
import {
  planCostItems,
  RoutePlanApiError,
  routePlansAPI,
  type RoutePlan,
} from '@/services/api/routePlans';

import RoutePlanDrawer from './components/RoutePlanDrawer';
import { costTotal } from './components/RoutePlanCostItems';

/**
 * Route Planner (CR-2, T8) — the Master page for reusable route templates. A
 * plan is scoped to a client, so the page is a per-client list (client + search
 * in the URL) feeding the create/edit drawer. Deactivation only hides a plan
 * from pickers (CR2-D16, no hard delete); the lane write-back warning lives in
 * the drawer.
 */

const toNum = (v: string | number): number => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};
const fmtKm = (v: string | number): string =>
  `${toNum(v).toLocaleString('id-ID')} km`;

const codeCls = 'text-[13px] font-semibold tracking-tight text-text-strong-950';
const metaCls = 'text-[11px] text-text-sub-600';

export interface RoutePlannerPageProps {
  /** Tests turn the server legs call off inside the drawer. */
  enableLegs?: boolean;
}

const RoutePlannerPage: React.FC<RoutePlannerPageProps> = ({
  enableLegs = true,
}) => {
  const [clientId, setClientId] = useUrlParam<string>('clientId', '');
  const {
    value: search,
    setValue: setSearch,
    committed: debouncedSearch,
  } = useUrlSearch('q', { delay: 500 });
  const [showInactive, setShowInactive] = useUrlBool('inactive', false);

  const clients = useProvidersDropdown(true);
  const numericClientId = useMemo(() => {
    const n = Number(clientId);
    return clientId && Number.isFinite(n) && n > 0 ? n : null;
  }, [clientId]);

  const [plans, setPlans] = useState<RoutePlan[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [chargedPositioning, setChargedPositioning] = useState<boolean | null>(
    null
  );

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState<RoutePlan | null>(null);
  const [confirmDeactivate, setConfirmDeactivate] = useState<RoutePlan | null>(
    null
  );
  const [mutating, setMutating] = useState(false);

  const fetchPlans = useCallback(async () => {
    if (numericClientId == null) {
      setPlans([]);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const rows = await routePlansAPI.list({
        clientId: numericClientId,
        search: debouncedSearch || undefined,
        ...(showInactive ? {} : { active: true }),
      });
      setPlans(rows);
    } catch (err) {
      setPlans([]);
      setError(
        err instanceof RoutePlanApiError || err instanceof Error
          ? err.message
          : 'Gagal memuat rute'
      );
    } finally {
      setLoading(false);
    }
  }, [numericClientId, debouncedSearch, showInactive]);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  // The client's pool-leg rule powers the drawer's charged banner + km charged.
  useEffect(() => {
    if (numericClientId == null) {
      setChargedPositioning(null);
      return;
    }
    let cancelled = false;
    ujpAPI
      .listClientConfigs()
      .then((rows) => {
        if (cancelled) return;
        const row = rows.find((r) => r.clientId === numericClientId);
        setChargedPositioning(row?.chargedPositioning ?? false);
      })
      .catch(() => !cancelled && setChargedPositioning(false));
    return () => {
      cancelled = true;
    };
  }, [numericClientId]);

  const openCreate = () => {
    setEditing(null);
    setDrawerOpen(true);
  };
  const openEdit = (plan: RoutePlan) => {
    setEditing(plan);
    setDrawerOpen(true);
  };

  const handleSaved = () => {
    setDrawerOpen(false);
    setEditing(null);
    fetchPlans();
  };

  const setActive = async (plan: RoutePlan, active: boolean) => {
    setMutating(true);
    try {
      await routePlansAPI.update(plan.id, { active });
      toast.success(active ? 'Rute diaktifkan' : 'Rute dinonaktifkan');
      setConfirmDeactivate(null);
      fetchPlans();
    } catch (err) {
      toast.error('Gagal memperbarui rute', {
        description: err instanceof Error ? err.message : undefined,
      });
    } finally {
      setMutating(false);
    }
  };

  const columns: TableDataColumn<RoutePlan>[] = [
    {
      key: 'name',
      title: 'Nama rute',
      render: (_v, r) => <span className={codeCls}>{r.name}</span>,
    },
    {
      key: 'route',
      title: 'Rute',
      render: (_v, r) => {
        const first = r.stops[0]?.name ?? '—';
        const last = r.stops[r.stops.length - 1]?.name ?? '—';
        return (
          <div className="min-w-0">
            <p className="truncate max-w-[280px] text-[13px] text-text-strong-950">
              {first} <span className="text-text-soft-400">→</span> {last}
            </p>
            <p className={metaCls}>{r.stops.length} stop</p>
          </div>
        );
      },
    },
    {
      key: 'totalKm',
      title: 'KM total',
      align: 'right',
      render: (_v, r) => (
        <span className="font-mono text-[12px] tabular-nums text-text-sub-600 whitespace-nowrap">
          {fmtKm(r.totalKm)}
        </span>
      ),
    },
    {
      key: 'kmCharged',
      title: 'KM ditagih',
      align: 'right',
      // The server stores km_charged with clientDefault=false (the planner API
      // has no client rule). Once the client's rule is known here, show the km
      // the UJP will actually bill (CR-8b position rule), so the list agrees
      // with the wizard; fall back to the stored value until the rule loads.
      render: (_v, r) => (
        <span className="font-mono text-[12px] tabular-nums text-text-strong-950 whitespace-nowrap">
          {fmtKm(
            chargedPositioning == null
              ? r.kmCharged
              : sumChargedLegs(
                  r.stops,
                  r.legs.map((l) => ({
                    from: l.fromSeq,
                    to: l.toSeq,
                    km: Number(l.km) || 0,
                  })),
                  (st) => st.seq,
                  chargedPositioning
                )
          )}
        </span>
      ),
    },
    {
      key: 'costItems',
      title: 'Biaya',
      align: 'right',
      // Σ of the plan's cost items — what picking it pre-fills in the UJP
      // Biaya step. "—" when the plan has none.
      render: (_v, r) => {
        const items = planCostItems(r);
        // The plan's Override BBM is not an operational cost (it replaces the
        // liter × price figure), so it rides in the tooltip, never in the total.
        const lines = items.map((i) => `${i.label}: ${formatRupiah(i.amount)}`);
        if (r.bbmFixOverride != null) {
          lines.push(`Override BBM: ${formatRupiah(r.bbmFixOverride)}`);
        }
        return (
          <span
            className="font-mono text-[12px] tabular-nums text-text-strong-950 whitespace-nowrap"
            title={lines.length > 0 ? lines.join('\n') : undefined}
          >
            {items.length > 0 ? formatRupiah(costTotal(items)) : '—'}
          </span>
        );
      },
    },
    {
      key: 'active',
      title: 'Status',
      render: (_v, r) =>
        r.active ? (
          <Badge status="success" size="sm">
            Aktif
          </Badge>
        ) : (
          <Badge status="neutral" size="sm">
            Nonaktif
          </Badge>
        ),
    },
    {
      key: 'actions',
      title: '',
      align: 'right',
      render: (_v, r) => (
        <div className="inline-flex items-center justify-end gap-1">
          <Button
            tone="neutral"
            style="stroke"
            size="xs"
            onClick={(e) => {
              e?.stopPropagation();
              openEdit(r);
            }}
          >
            Ubah
          </Button>
          {r.active ? (
            <Button
              tone="neutral"
              style="ghost"
              size="xs"
              onClick={(e) => {
                e?.stopPropagation();
                setConfirmDeactivate(r);
              }}
            >
              Nonaktifkan
            </Button>
          ) : (
            <Button
              tone="neutral"
              style="ghost"
              size="xs"
              onClick={(e) => {
                e?.stopPropagation();
                setActive(r, true);
              }}
            >
              Aktifkan
            </Button>
          )}
        </div>
      ),
    },
  ];

  const emptyState = error ? (
    <EmptyState size="sm">
      <EmptyStateTitle>Gagal memuat rute</EmptyStateTitle>
      <EmptyStateDescription>{error}</EmptyStateDescription>
      <EmptyStateActions>
        <Button tone="neutral" style="ghost" size="sm" onClick={fetchPlans}>
          Coba lagi
        </Button>
      </EmptyStateActions>
    </EmptyState>
  ) : numericClientId == null ? (
    <EmptyState size="sm">
      <EmptyStateTitle>Pilih client dulu</EmptyStateTitle>
      <EmptyStateDescription>
        Rute tersimpan per client. Pilih client untuk melihat rutenya.
      </EmptyStateDescription>
    </EmptyState>
  ) : (
    <EmptyState size="sm">
      <EmptyStateTitle>Belum ada rute untuk client ini</EmptyStateTitle>
      <EmptyStateDescription>
        Buat rute baru untuk dipakai di wizard UJP dan shipment 4W.
      </EmptyStateDescription>
      <EmptyStateActions>
        <Button size="sm" onClick={openCreate}>
          <RiAddLine />
          Buat rute
        </Button>
      </EmptyStateActions>
    </EmptyState>
  );

  return (
    <div className="space-y-5 p-6">
      <div className="flex flex-col gap-3 border-b border-stroke-soft-200 pb-5 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="label-eyebrow mb-2">Logistic · Master</p>
          <h1 className="text-[28px] font-extrabold leading-none tracking-tight text-text-strong-950">
            Route Planner
          </h1>
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-text-sub-600">
            Template rute per client dari lane Addresses. Dipakai di wizard UJP
            dan langkah stop shipment 4W.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button tone="neutral" style="stroke" onClick={fetchPlans}>
            <RiRefreshLine />
            Muat ulang
          </Button>
          <Button onClick={openCreate} disabled={numericClientId == null}>
            <RiAddLine />
            Buat rute
          </Button>
        </div>
      </div>

      <Card padding="none">
        <div className="flex flex-col gap-3 border-b border-stroke-soft-200 px-4 py-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="w-64">
            <SearchSelect
              options={clients.options.map((p) => ({
                value: String(p.id),
                label: p.name,
              }))}
              value={clientId || undefined}
              selectedLabelFallback={
                clientId ? clients.labelFor(clientId) : undefined
              }
              onChange={(v) => setClientId(v ? String(v) : '')}
              placeholder="Pilih client"
              emptyMessage={clients.error ?? 'Client tidak ditemukan'}
              loading={clients.loading}
              hasMore={clients.hasMore}
              disableLocalFilter
              onSearchChange={clients.setSearch}
              onLoadMore={clients.loadMore}
            />
          </div>
          <div className="flex flex-1 flex-wrap items-center justify-end gap-3">
            <label className="flex cursor-pointer items-center gap-2 text-[13px] text-text-sub-600">
              <Switch
                checked={showInactive}
                onCheckedChange={(v) => setShowInactive(v === true)}
              />
              Tampilkan nonaktif
            </label>
            <SearchField
              value={search}
              onChange={setSearch}
              placeholder="Cari nama rute…"
              size="sm"
              className="min-w-[220px] max-w-xs"
            />
          </div>
        </div>

        <div className="p-4">
          <TableData<RoutePlan>
            data={error ? [] : plans}
            columns={columns}
            loading={loading}
            size="sm"
            emptyState={emptyState}
            onRowClick={(r) => openEdit(r)}
          />
        </div>
      </Card>

      <RoutePlanDrawer
        open={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setEditing(null);
        }}
        clientId={numericClientId}
        clientName={clientId ? clients.labelFor(clientId) : undefined}
        chargedPositioning={chargedPositioning}
        plan={editing}
        onSaved={handleSaved}
        enableLegs={enableLegs}
      />

      <FormModal
        open={!!confirmDeactivate}
        onClose={() => setConfirmDeactivate(null)}
        icon={<RiRefreshLine />}
        title="Nonaktifkan rute?"
        subtitle={confirmDeactivate?.name}
        size="sm"
        footer={
          <div className="flex items-center justify-end gap-2">
            <Button
              tone="neutral"
              style="stroke"
              onClick={() => setConfirmDeactivate(null)}
              disabled={mutating}
            >
              Batal
            </Button>
            <Button
              tone="destructive"
              onClick={() =>
                confirmDeactivate && setActive(confirmDeactivate, false)
              }
              loading={mutating}
            >
              Nonaktifkan
            </Button>
          </div>
        }
      >
        <p className="text-sm text-text-sub-600">
          Rute disembunyikan dari picker UJP &amp; 4W. UJP lama tetap memakai
          snapshot-nya. Bisa diaktifkan lagi kapan saja.
        </p>
      </FormModal>
    </div>
  );
};

export default RoutePlannerPage;
